// src/api/shiftsApi.ts

import type { ShiftDto } from "../components/shifts/ShiftCard";
import type { ShiftParticipantsResponse } from "../components/shifts/ParticipantsModal";
import { getAuthHeaders } from "../api/authHelper";

export type EventItem = { eventID: number; eventName: string };

export type FunctionItem = {
    functionId: number;
    functionName: string;
    eventID: number;
    functionCategoryId: number;
    categoryName: string;
    description: string;
};

export type ShiftUpsertDto = {
    functionId: number;
    shiftsName: string;
    startTime: string;
    endTime: string;
    maxParticipants: number;
    waitlist: boolean;
    ticketID?: number | null;
    endsOneHourAfterPlay: boolean;
};

// ---------- helpers ----------
async function readText(r: Response): Promise<string> {
    try {
        return await r.text();
    } catch {
        return "";
    }
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
    const r = await fetch(url, init);
    const text = await readText(r);

    if (!r.ok) {
        throw new Error(`HTTP ${r.status} ${r.statusText}. Body: ${text}`);
    }

    if (!text) return [] as unknown as T;

    try {
        return JSON.parse(text) as T;
    } catch {
        return text as unknown as T;
    }
}

function qs(params: Record<string, string | number | undefined | null>) {
    const p = new URLSearchParams();

    Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "") return;
        p.set(k, String(v));
    });

    const s = p.toString();
    return s ? `?${s}` : "";
}

// ---------- API ----------

// Public eller login afh�ngigt af din EventController.
// Du har pt [AllowAnonymous] p� /api/events, s� auth er ikke n�dvendig her.
export async function getEvents(apiUrl: string): Promise<EventItem[]> {
    const data = await fetchJson<EventItem[]>(`${apiUrl}/api/events`);
    return Array.isArray(data) ? data : [];
}

// Admin endpoint hos dig
export async function getFunctions(apiUrl: string, eventId: number): Promise<FunctionItem[]> {
    const data = await fetchJson<FunctionItem[]>(
        `${apiUrl}/api/events/${eventId}/functions`,
        {
            headers: getAuthHeaders(),
        }
    );

    return Array.isArray(data) ? data : [];
}

// Du har pt [AllowAnonymous] p� this endpoint
export async function getShiftsWithCounts(
    apiUrl: string,
    eventId: number,
    opts?: { functionId?: number; date?: string }
): Promise<ShiftDto[]> {
    const query = qs({
        functionId: opts?.functionId,
        date: opts?.date,
    });

    const url = `${apiUrl}/api/events/${eventId}/shifts/with-counts${query}`;
    const data = await fetchJson<ShiftDto[]>(url);

    return Array.isArray(data) ? data : [];
}

// Hvis participants er l�st i ShiftsController, kr�ver den auth.
// Jeg ville sende auth med her.
export async function getShiftParticipants(
    apiUrl: string,
    shiftId: number
): Promise<ShiftParticipantsResponse> {
    return await fetchJson<ShiftParticipantsResponse>(
        `${apiUrl}/api/shifts/${shiftId}/participants`,
        {
            headers: getAuthHeaders(),
        }
    );
}

// Admin
export async function createShift(apiUrl: string, payload: ShiftUpsertDto): Promise<void> {
    await fetchJson<void>(`${apiUrl}/api/shifts`, {
        method: "POST",
        headers: getAuthHeaders({
            "Content-Type": "application/json",
        }),
        body: JSON.stringify(payload),
    });
}

// Admin
export async function updateShift(
    apiUrl: string,
    shiftId: number,
    payload: ShiftUpsertDto
): Promise<void> {
    await fetchJson<void>(`${apiUrl}/api/shifts/${shiftId}`, {
        method: "PUT",
        headers: getAuthHeaders({
            "Content-Type": "application/json",
        }),
        body: JSON.stringify(payload),
    });
}

// Admin
export async function deleteShift(apiUrl: string, shiftId: number): Promise<void> {
    await fetchJson<void>(`${apiUrl}/api/shifts/${shiftId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
    });
}

// Protected
export async function getAllowedEventIds(apiUrl: string, userId: number): Promise<number[]> {
    const res = await fetch(`${apiUrl}/api/users/allowed-events?userId=${userId}`, {
        headers: getAuthHeaders(),
    });

    if (!res.ok) throw new Error("Failed to load allowed events");

    return await res.json();
}